import { fetchRateInputs } from "./rateInputsClient";
import {
  currentMonthStart,
  fetchPagesConcurrently,
  fscProposalFromRates,
  type FscProposal,
  type InvoiceGroupRow,
} from "./revenueTab.mjs";
import { isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * Revenue tab: data access. Thin on purpose; every decision about what a line
 * means is in revenueTab.mjs.
 *
 * GROUPED READS. PostgREST groups by every plain column in the select and
 * sums bill_amount_net per group, so a page is one row per group, not one per
 * invoice line. Every read:
 *   - is bound to the tenant (scopeTenant, MER-245);
 *   - counts only the current version of a line (MER-838: is_current_version
 *     is not false);
 *   - stops at the first day of the current month, so a month that has not
 *     ended is never read (MER-553 / MER-693);
 *   - orders by every group column, so pages are stable;
 *   - reads every page; a read that would need more than its page cap throws
 *     and the view is withheld with a note, never shown from a partial set.
 *
 * TWO SIZES. The line-grain reads (month x division x line x class x line
 * text) are small and back most of the tab. The account-grain reads are the
 * big ones (one row per account per month per division per line), so they
 * run only when an account view mounts, one earned month per request stream
 * and a few months at a time (PostgREST re-runs the aggregate for every page,
 * so small partitions keep each request cheap).
 *
 * Both are fetched once per tenant per page load and shared through a
 * module-level promise, so every widget on the tab cuts from the same rows.
 */

const TABLE = "invoice";
const PAGE_SIZE = 1000;
const CONCURRENCY = 6;
const LINE_MAX_PAGES = 80;
/** Per earned month: a closed month of account-grain groups, paged. */
const ACCOUNT_MONTH_MAX_PAGES = 100;
/** Months read at once, each several pages at a time. */
const MONTH_CONCURRENCY = 4;
const MONTH_PAGE_CONCURRENCY = 3;
const NAME_MAX_PAGES = 200;

type Row = Record<string, unknown>;

export class RevenueReadError extends Error {
  /** True when the table or a column is not there yet (awaiting source data). */
  readonly missing: boolean;
  /** True when the read hit its page cap and was withheld. */
  readonly truncated: boolean;
  constructor(message: string, opts: { missing?: boolean; truncated?: boolean } = {}) {
    super(message);
    this.name = "RevenueReadError";
    this.missing = opts.missing === true;
    this.truncated = opts.truncated === true;
  }
}

function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

async function grouped(
  tenantId: string,
  groupCols: string[],
  measures: string,
  opts: {
    negativeOnly?: boolean;
    surchargeOnly?: boolean;
    /** One earned month (its YYYY-MM-01 date) instead of every closed month. */
    month?: string;
    concurrency?: number;
    maxPages: number;
    label: string;
  },
  today: Date,
): Promise<InvoiceGroupRow[]> {
  const supabase = getSupabase();
  const select = `${groupCols.join(",")},${measures}`;
  const upper = currentMonthStart(today);
  let result;
  try {
    result = await fetchPagesConcurrently(
      (from: number, to: number) => {
        let q: any = scopeTenant((supabase.from(TABLE) as any).select(select), tenantId);
        q = q
          .not("is_current_version", "is", false)
          .not("revenue_recognition_date", "is", null)
          .lt("revenue_recognition_date", upper);
        if (opts.month) q = q.eq("revenue_recognition_date", opts.month);
        if (opts.negativeOnly) q = q.lt("bill_amount_net", 0);
        if (opts.surchargeOnly) q = q.eq("ar_type", "Surcharge");
        for (const col of groupCols) q = q.order(col, { ascending: true, nullsFirst: true });
        return q.range(from, to);
      },
      {
        pageSize: PAGE_SIZE,
        maxPages: opts.maxPages,
        concurrency: opts.concurrency ?? CONCURRENCY,
      },
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new RevenueReadError(message, { missing: isMissing(message) });
  }
  if (result.truncated) {
    throw new RevenueReadError(
      `${opts.label} exceeded ${PAGE_SIZE * opts.maxPages} grouped rows; not shown rather ` +
        "than shown partially.",
      { truncated: true },
    );
  }
  return result.rows as InvoiceGroupRow[];
}

/** The line-grain group: month, division, line, and everything the line text needs. */
export const LINE_GROUP = [
  "revenue_recognition_date",
  "division",
  "line_of_business",
  "ar_type",
  "surcharge_name",
  "charge_code",
  "service_code",
  "account_class",
];

export interface RevenueLines {
  all: InvoiceGroupRow[];
  negative: InvoiceGroupRow[];
}

const lineCache = new Map<string, Promise<RevenueLines>>();

/** Every closed month's lines at line grain, and the negative lines alone. */
export function fetchRevenueLines(
  tenantId: string,
  today: Date = new Date(),
): Promise<RevenueLines> {
  const key = `${tenantId}|${currentMonthStart(today)}`;
  const hit = lineCache.get(key);
  if (hit) return hit;
  const promise = Promise.all([
    grouped(
      tenantId,
      LINE_GROUP,
      "bill_amount_net.sum(),invoice_line_id.count()",
      { maxPages: LINE_MAX_PAGES, label: "Revenue lines" },
      today,
    ),
    grouped(
      tenantId,
      LINE_GROUP,
      "bill_amount_net.sum()",
      { negativeOnly: true, maxPages: LINE_MAX_PAGES, label: "Negative revenue lines" },
      today,
    ),
  ]).then(([all, negative]) => ({ all, negative }));
  // A failed read must not stick: the next mount retries.
  promise.catch(() => lineCache.delete(key));
  lineCache.set(key, promise);
  return promise;
}

export interface RevenueAccounts {
  all: InvoiceGroupRow[];
  negative: InvoiceGroupRow[];
  fuel: InvoiceGroupRow[];
  names: Map<string, string>;
  /** The tenant's fuel surcharge proposal rule, from its private rate inputs. */
  proposal: FscProposal | null;
}

async function fetchNames(tenantId: string): Promise<Map<string, string>> {
  const supabase = getSupabase();
  let result;
  try {
    result = await fetchPagesConcurrently(
      (from: number, to: number) => {
        const q: any = scopeTenant(
          (supabase.from("customer") as any).select("customer_id,customer_name"),
          tenantId,
        );
        return q.order("customer_id", { ascending: true }).range(from, to);
      },
      { pageSize: PAGE_SIZE, maxPages: NAME_MAX_PAGES, concurrency: CONCURRENCY },
    );
  } catch {
    // Names are a label, not a figure: without them the account id is shown.
    return new Map();
  }
  const out = new Map<string, string>();
  for (const row of result.rows as Row[]) {
    const id = row.customer_id == null ? "" : String(row.customer_id);
    const name = typeof row.customer_name === "string" ? row.customer_name.trim() : "";
    if (id && name && !out.has(id)) out.set(id, name);
  }
  return out;
}

/** The closed earned months the tenant has lines in, as YYYY-MM-01 dates. */
async function closedMonths(tenantId: string, today: Date): Promise<string[]> {
  const rows = await grouped(
    tenantId,
    ["revenue_recognition_date"],
    "invoice_line_id.count()",
    { maxPages: 5, label: "Revenue months" },
    today,
  );
  return rows
    .map((r) => (typeof r.revenue_recognition_date === "string" ? r.revenue_recognition_date : ""))
    .filter(Boolean);
}

/**
 * One grouped read per earned month, a few months at a time. The account
 * grain is large (one row per account per month per division per line), and
 * PostgREST re-runs the whole aggregate for every page it serves, so paging
 * one month at a time keeps each request small. Every month is read in full
 * or the read throws; the parts are joined in month order.
 */
async function groupedByMonth(
  tenantId: string,
  months: string[],
  groupCols: string[],
  measures: string,
  opts: { negativeOnly?: boolean; surchargeOnly?: boolean; label: string },
  today: Date,
): Promise<InvoiceGroupRow[]> {
  const parts: InvoiceGroupRow[][] = new Array(months.length);
  for (let i = 0; i < months.length; i += MONTH_CONCURRENCY) {
    const wave = months.slice(i, i + MONTH_CONCURRENCY);
    const got = await Promise.all(
      wave.map((month) =>
        grouped(
          tenantId,
          groupCols,
          measures,
          {
            ...opts,
            month,
            concurrency: MONTH_PAGE_CONCURRENCY,
            maxPages: ACCOUNT_MONTH_MAX_PAGES,
            label: `${opts.label} (${month.slice(0, 7)})`,
          },
          today,
        ),
      ),
    );
    got.forEach((rows, j) => {
      parts[i + j] = rows;
    });
  }
  return parts.flat();
}

const accountCache = new Map<string, Promise<RevenueAccounts>>();

/**
 * The account-grain reads: every line by (account, month, division, line);
 * the negative lines with their text, so the credit rule can be applied per
 * account; the surcharge lines by name, for the fuel surcharge views; and the
 * account names.
 */
export function fetchRevenueAccounts(
  tenantId: string,
  today: Date = new Date(),
): Promise<RevenueAccounts> {
  const key = `${tenantId}|${currentMonthStart(today)}`;
  const hit = accountCache.get(key);
  if (hit) return hit;
  const promise = closedMonths(tenantId, today).then((months) =>
    Promise.all([
      groupedByMonth(
        tenantId,
        months,
        ["customer_id", "revenue_recognition_date", "division", "line_of_business"],
        "bill_amount_net.sum()",
        { label: "Account revenue" },
        today,
      ),
      grouped(
        tenantId,
        [
          "customer_id",
          "revenue_recognition_date",
          "division",
          "line_of_business",
          "ar_type",
          "surcharge_name",
          "charge_code",
          "service_code",
        ],
        "bill_amount_net.sum()",
        { negativeOnly: true, maxPages: LINE_MAX_PAGES, label: "Account credits" },
        today,
      ),
      groupedByMonth(
        tenantId,
        months,
        [
          "customer_id",
          "revenue_recognition_date",
          "division",
          "line_of_business",
          "surcharge_name",
        ],
        "bill_amount_net.sum()",
        { surchargeOnly: true, label: "Account surcharges" },
        today,
      ),
      fetchNames(tenantId),
      // Pricing is read at runtime from tenant-RLS storage, never shipped in
      // the public manifest. An unreadable table degrades to no rule.
      fetchRateInputs(tenantId).then(fscProposalFromRates),
    ]).then(([all, negative, fuel, names, proposal]) => ({
      all,
      negative,
      fuel,
      names,
      proposal,
    })),
  );
  promise.catch(() => accountCache.delete(key));
  accountCache.set(key, promise);
  return promise;
}
